import React, { useContext } from "react";
import {
  EuiButtonEmpty,
  EuiEmptyPrompt,
  EuiFlexGroup,
  EuiFlexItem,
  EuiLoadingSpinner,
  EuiProgress,
  EuiSpacer,
  EuiText,
  EuiTitle,
} from "@elastic/eui";
import { AnketaContext, EvaluationContext } from "../context_provider";
import { countIssues } from "../issues";
import { useAnketaI18n } from "src/internationalization";

/**
 * Progress line above the form: how much is answered, and - once the user asks
 * or has saved - a jump into the fields that are invalid.
 */
const QuestionnaireProgress = (): JSX.Element | null => {
  const ctx = useContext(AnketaContext);
  const evalCtx = useContext(EvaluationContext);
  const { translate } = useAnketaI18n();
  const issues = countIssues(ctx.catalog, ctx);

  if (issues.total === 0) {
    return null;
  }
  const answered = issues.total - issues.missing;

  return (
    <>
      <EuiFlexGroup alignItems="center" gutterSize="m" responsive={false} wrap>
        <EuiFlexItem style={{ minWidth: 160, maxWidth: 360 }}>
          <EuiProgress
            value={answered}
            max={issues.total}
            size="s"
            color={answered === issues.total ? "success" : "primary"}
            aria-label={translate("anketa.questionnaire.progress.aria", {
              defaultMessage: "Answered fields",
            })}
          />
        </EuiFlexItem>
        <EuiFlexItem grow={false}>
          <EuiText size="s" color="subdued">
            {translate("anketa.questionnaire.progress.answered", {
              values: { answered, total: issues.total },
              defaultMessage: "{{answered}} of {{total}} fields answered",
            })}
          </EuiText>
        </EuiFlexItem>
        {issues.invalid > 0 && (
          <EuiFlexItem grow={false}>
            <EuiButtonEmpty
              size="s"
              color="danger"
              iconType="alert"
              onClick={() => evalCtx.setRevealIssues(true)}
            >
              {translate("anketa.questionnaire.progress.invalid", {
                values: { count: issues.invalid },
                defaultMessage: "{{count}} invalid answers - show",
              })}
            </EuiButtonEmpty>
          </EuiFlexItem>
        )}
      </EuiFlexGroup>
      <EuiSpacer size="s" />
    </>
  );
};

export const CatalogCard = (): JSX.Element => {
  const ctx = useContext(AnketaContext);
  // Subscribing re-renders the form on every committed change. The form used
  // to be copied into state from an effect, which rendered it one frame late
  // (and once more per change).
  useContext(EvaluationContext);
  const { translate } = useAnketaI18n();

  if (!ctx.catalog || ctx.catalog.children.size === 0) {
    return (
      <EuiEmptyPrompt
        iconType="editorStrike"
        title={
          <EuiFlexGroup
            direction="column"
            justifyContent="center"
            alignItems="center"
          >
            <EuiFlexItem grow={false}>
              <EuiTitle>
                <h2>
                  {translate("anketa.questionnaire.card.catalog.loading", {
                    defaultMessage: "Loading...",
                  })}
                </h2>
              </EuiTitle>
            </EuiFlexItem>
            <EuiFlexItem grow={false}>
              <EuiLoadingSpinner size="xl" />
            </EuiFlexItem>
          </EuiFlexGroup>
        }
      />
    );
  }

  return (
    <>
      <QuestionnaireProgress />
      {ctx.childrenForItem(ctx.catalog)}
    </>
  );
};
